const API_BASE = import.meta.env.VITE_API_URL || '';
import { MOCK_ENABLED, mockRequest, mockDownload } from './mock';

class ApiService {
  private async request(path: string, options: RequestInit = {}): Promise<any> {
    if (MOCK_ENABLED) {
      return mockRequest(path, options.method || 'GET', JSON.parse((options.body as string) || 'null'));
    }
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Request failed' }));
      const e: any = new Error(err.error || err.message || 'Request failed');
      e.status = res.status;
      e.reauth = !!err.reauth;
      throw e;
    }
    return res.json();
  }

  // Auth
  async login(password: string) {
    return this.request('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) });
  }
  async logout() {
    return this.request('/api/auth/logout', { method: 'POST' });
  }
  async checkAuth() {
    return this.request('/api/auth/check');
  }
  async getGoogleAuthUrl() {
    if (MOCK_ENABLED) return mockRequest('/api/auth/google', 'GET', null);
    return this.request('/api/auth/google');
  }

  // Drive
  async listFiles(folderId?: string) {
    return this.request(`/api/drive/files${folderId ? `?folderId=${folderId}` : ''}`);
  }
  async getFile(fileId: string) {
    return this.request(`/api/drive/files/${fileId}`);
  }
  async downloadFile(fileId: string) {
    if (MOCK_ENABLED) {
      const blob = mockDownload(fileId);
      return blob.text();
    }
    const res = await fetch(`${API_BASE}/api/drive/files/${fileId}/download`, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Download failed' }));
      throw new Error(err.message);
    }
    return res.text();
  }
  async downloadFileBlob(fileId: string) {
    if (MOCK_ENABLED) {
      return mockDownload(fileId);
    }
    const res = await fetch(`${API_BASE}/api/drive/files/${fileId}/download`, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Download failed' }));
      throw new Error(err.message);
    }
    return res.blob();
  }
  async uploadFile(name: string, content: string, parentId?: string, mimeType?: string, isBase64 = false) {
    return this.request('/api/drive/files', {
      method: 'POST',
      body: JSON.stringify({ name, content, parentId, mimeType, isBase64 }),
    });
  }
  async createFolder(name: string, parentId?: string) {
    return this.request('/api/drive/folders', { method: 'POST', body: JSON.stringify({ name, parentId }) });
  }
  async renameFile(fileId: string, name: string) {
    return this.request(`/api/drive/files/${fileId}`, { method: 'PUT', body: JSON.stringify({ name }) });
  }
  async updateFileContent(fileId: string, content: string, mimeType?: string) {
    return this.request(`/api/drive/files/${fileId}`, {
      method: 'PUT',
      body: JSON.stringify({ content, mimeType, updateContent: true }),
    });
  }
  async deleteFile(fileId: string) {
    return this.request(`/api/drive/files/${fileId}`, { method: 'DELETE' });
  }
  async moveFile(fileId: string, newParentId?: string) {
    return this.request(`/api/drive/files/${fileId}/move`, { method: 'POST', body: JSON.stringify({ newParentId }) });
  }
  async copyFile(fileId: string, newParentId?: string) {
    return this.request(`/api/drive/files/${fileId}/copy`, { method: 'POST', body: JSON.stringify({ newParentId }) });
  }
  async searchFiles(query: string) {
    return this.request(`/api/drive/search?query=${encodeURIComponent(query)}`);
  }

  // Projects
  async listProjects() {
    return this.request('/api/projects');
  }
  async createProject(name: string) {
    return this.request('/api/projects', { method: 'POST', body: JSON.stringify({ name }) });
  }
  async getProject(id: string) {
    return this.request(`/api/projects/${id}`);
  }
  async deleteProject(id: string) {
    return this.request(`/api/projects/${id}`, { method: 'DELETE' });
  }
  async syncProjectDown(id: string) {
    return this.request(`/api/projects/${id}/sync-down`, { method: 'POST' });
  }
  async syncProjectUp(id: string) {
    return this.request(`/api/projects/${id}/sync-up`, { method: 'POST' });
  }
  async getProjectFiles(id: string) {
    return this.request(`/api/projects/${id}/files`);
  }
  async getProjectFile(id: string, path: string) {
    return this.request(`/api/projects/${id}/file?path=${encodeURIComponent(path)}`);
  }
  async updateProjectFile(id: string, path: string, content: string) {
    return this.request(`/api/projects/${id}/file`, {
      method: 'PUT',
      body: JSON.stringify({ path, content }),
    });
  }
  async deleteProjectFile(id: string, path: string) {
    return this.request(`/api/projects/${id}/file?path=${encodeURIComponent(path)}`, { method: 'DELETE' });
  }
  async createProjectFolder(id: string, path: string) {
    return this.request(`/api/projects/${id}/folder`, { method: 'POST', body: JSON.stringify({ path }) });
  }
  async uploadProjectFile(id: string, path: string, content: string, isBase64 = false) {
    return this.request(`/api/projects/${id}/upload`, {
      method: 'POST',
      body: JSON.stringify({ path, content, isBase64 }),
    });
  }
  async renameProjectEntry(id: string, from: string, to: string) {
    return this.request(`/api/projects/${id}/rename`, {
      method: 'POST',
      body: JSON.stringify({ from, to }),
    });
  }

  // Settings
  async getSettings() {
    return this.request('/api/settings');
  }
  async updateSettings(settings: Record<string, string>) {
    return this.request('/api/settings', { method: 'PUT', body: JSON.stringify(settings) });
  }
}

export const api = new ApiService();